/**
 * Timezone-consistent date-range bounds for filtering workouts by `plannedAt`.
 *
 * A workout's `plannedAt` is a **calendar date** (the day the user plans it for),
 * authored from the device's LOCAL date via `formatISO(date, { representation:
 * 'date' })` and stored two ways in the local PowerSync SQLite:
 *  - fresh local create:    `"2026-07-13"`                (bare date — `WorkoutMutations.create` stores the form value verbatim)
 *  - after a Postgres sync: `"2026-07-13T00:00:00.000Z"`  (the DateTime column serializes the date to UTC-midnight)
 *
 * `WorkoutQueries.findByDateRange` filters `plannedAt >= fromDate AND plannedAt <=
 * toDate` with SQLite's default BINARY (lexicographic) string comparison. The
 * bounds must therefore be expressed so that BOTH stored formats of an in-range
 * day compare inside `[fromDate, toDate]`, in ANY device timezone:
 *  - `fromDate` = the local calendar-date key (`"2026-07-13"`). Both `"2026-07-13"`
 *    and `"2026-07-13T00:00:00.000Z"` are `>=` it.
 *  - `toDate`   = the last local day's key + end-of-day (`"2026-07-13T23:59:59.999Z"`).
 *    Both formats of that day are `<=` it, while the next day is not.
 *
 * The previous call sites used `startOfWeek(now).toISOString()` — a local-midnight
 * *instant* converted to UTC (e.g. `"2026-07-13T07:00:00.000Z"` west of UTC) —
 * which sorts AFTER a UTC-midnight `plannedAt`, silently dropping a workout planned
 * "today"/on the week-start for every user behind UTC. Comparing calendar dates,
 * not instants, is the fix.
 */
import { format, parseISO, startOfWeek } from 'date-fns';

export interface PlannedDateRangeBounds {
  /** Inclusive lower bound: a local calendar-date key, e.g. `"2026-07-13"`. */
  fromDate: string;
  /** Inclusive upper bound: last day's end-of-day, e.g. `"2026-07-13T23:59:59.999Z"`. */
  toDate: string;
}

export class PlannedDateRange {
  /** Appended to the last day's key so a UTC-midnight `plannedAt` of that day is included. */
  private static readonly END_OF_DAY_SUFFIX = 'T23:59:59.999Z';

  /** The device-LOCAL calendar-date key, matching how `plannedAt` is authored. */
  private static dateKey(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

  /** Bounds spanning `[fromDay, toDay]` inclusive, by local calendar date. */
  static forDays(fromDay: Date, toDay: Date): PlannedDateRangeBounds {
    return {
      fromDate: PlannedDateRange.dateKey(fromDay),
      toDate:
        PlannedDateRange.dateKey(toDay) + PlannedDateRange.END_OF_DAY_SUFFIX,
    };
  }

  /** Bounds covering a single local calendar day. */
  static forSingleDay(date: Date): PlannedDateRangeBounds {
    return PlannedDateRange.forDays(date, date);
  }

  /** Bounds from the start of `now`'s (Monday-based) week through `now`'s day. */
  static forWeekToDate(now: Date): PlannedDateRangeBounds {
    return PlannedDateRange.forDays(startOfWeek(now, { weekStartsOn: 1 }), now);
  }

  /**
   * Mirrors `findByDateRange`'s SQL predicate (`plannedAt >= fromDate AND <=
   * toDate`, lexicographic) for in-memory use and tests.
   */
  static contains(bounds: PlannedDateRangeBounds, plannedAt: string): boolean {
    return plannedAt >= bounds.fromDate && plannedAt <= bounds.toDate;
  }

  /**
   * Parses a stored `plannedAt` to the LOCAL `Date` at midnight of its CALENDAR
   * date, ignoring any time-of-day / zone suffix. `parseISO` on the full
   * `"2026-07-14T00:00:00.000Z"` is a UTC *instant* that renders as the previous
   * day on devices behind UTC — so a workout planned "today" was grouped under
   * "Yesterday". Slicing to the `yyyy-MM-dd` key first (then parsing it as a
   * date-only value → local midnight) keeps it on its own calendar day in any zone.
   * Use this for any day grouping/labelling of `plannedAt`.
   */
  static toCalendarDate(plannedAt: string | Date): Date {
    const key =
      typeof plannedAt === 'string'
        ? plannedAt.slice(0, 10)
        : format(plannedAt, 'yyyy-MM-dd');
    return parseISO(key);
  }
}
